import { useState } from 'react';
import { useQuery } from 'convex/react';
import type { FunctionReturnType } from 'convex/server';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';
import { useCurrentUser } from './useCurrentUser';

export type BillingSummary = FunctionReturnType<typeof api.billing.summary>;
export type Receipt = FunctionReturnType<typeof api.billing.receipts>[number];
export type Purchase = FunctionReturnType<typeof api.billing.purchases>[number];

/** The plan as every plan surface reads it; skips until the users row exists. */
export function useBillingSummary() {
  const { user } = useCurrentUser();
  return useQuery(api.billing.summary, user ? {} : 'skip');
}

export function useReceipts() {
  const { user } = useCurrentUser();
  return useQuery(api.billing.receipts, user ? {} : 'skip');
}

export function usePurchases() {
  const { user } = useCurrentUser();
  return useQuery(api.billing.purchases, user ? {} : 'skip');
}

/** A pack as the checkout needs it: enough to price and name it. */
export interface PackForSale {
  _id: Id<'packs'>;
  name: string;
  priceCents: number;
  songCount: number;
}

/** Which checkout a CTA opens: the Beta subscription, its cancellation, or a
 * pack sold outright. Each confirms one server action, never a shared one. */
export type CheckoutVariant = { kind: 'beta' } | { kind: 'cancel' } | { kind: 'pack'; pack: PackForSale };

/** Where Stripe sends the user back: this page. */
function returnHere() {
  return { origin: window.location.origin, returnPath: `${window.location.pathname}${window.location.search}` };
}

/**
 * The one-shot billing actions. `confirm` runs the action behind a checkout
 * variant and follows Stripe's page when the server hands one back; a change
 * made in place resolves with no page and the summary query updates itself.
 */
export function useBillingActions() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run<T>(call: () => Promise<T>) {
    setPending(true);
    setError(null);
    const result = await toResult(call);
    setPending(false);
    if (!result.ok) setError(result.error.message);
    return result;
  }

  async function follow(call: () => Promise<{ url: string | null }>) {
    const result = await run(call);
    if (result.ok && result.value.url) window.location.assign(result.value.url);
    return result.ok;
  }

  return {
    pending,
    error,
    confirm: (variant: CheckoutVariant) =>
      follow(() => {
        const back = returnHere();
        switch (variant.kind) {
          case 'beta':
            return convex.action(api.billing.subscribe, back);
          case 'cancel':
            return convex.action(api.billing.cancelSubscription, {});
          case 'pack':
            return convex.action(api.billing.buyPack, { ...back, packId: variant.pack._id });
        }
      }),
    keep: () => follow(() => convex.action(api.billing.keepSubscription, {})),
    openPortal: () => follow(() => convex.action(api.billing.openBillingPortal, returnHere())),
  };
}
